import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, RefreshCw, X, FileSpreadsheet } from "lucide-react";

export const inrC = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
export const fmtPeriodC = (s) => String(s || "—").replace(/_/g, " ").replace(/\bto\b/, "→");
const sp = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
const lr = (a) => (Array.isArray(a) && a[0] != null ? `${sp(a[0])} / ${sp(a[1])}` : "—");
const fmtIst = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) + " IST" : "—");

const xlsxName = (label) => {
  const [d1, m1, , d2, m2, y] = String(label).split("_");
  const t = (m) => m.charAt(0).toUpperCase() + m.slice(1);
  return `Samara_Closing_${d1}-${t(m1)}_to_${d2}-${t(m2)}-${y}.xlsx`;
};

/** Download one week's payout list as Excel (saved rows if finalized, else the live preview). */
export function XlsxButton({ label, testid, compact = false }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/weekly-closing/xlsx", { params: { period_label: label }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: xlsxName(label) });
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Excel download failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={download} disabled={busy} data-testid={testid} title="Download Excel"
      className={`inline-flex items-center gap-1 rounded-sm border border-emerald-600/40 text-emerald-800 hover:bg-emerald-50 disabled:opacity-50 transition-colors ${compact ? "text-[10px] px-1.5 py-0.5" : "text-[11px] uppercase tracking-widest font-bold px-3 py-2"}`}>
      {busy ? <RefreshCw size={11} className="animate-spin" /> : <FileSpreadsheet size={compact ? 11 : 13} />} {compact ? "Excel" : "Download Excel"}
    </button>
  );
}

/** Member-wise weekly-closing preview + explicit "Confirm Weekly Closing". */
export function WeeklyClosingPreview({ preview, onClose, onConfirmed }) {
  const p = preview;
  const rows = p.detail || [];
  const [asking, setAsking] = useState(false);
  const done = p.already_finalized;
  return (
    <div data-testid="closing-preview-panel" className="border-b border-border bg-slate-50/60">
      <div className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap border-b border-border bg-white">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-royal font-bold">Preview · nothing saved yet</div>
          <div className="font-serif text-lg text-navy" data-testid="closing-preview-period">{fmtPeriodC(p.period_label)}</div>
          <div className="text-[11px] text-navy/70">Generated {fmtIst(p.generated_at)} · same figures as each member's Account Info</div>
        </div>
        <button type="button" onClick={onClose} data-testid="closing-preview-close" className="p-1.5 text-navy/60 hover:text-navy transition-colors"><X size={16} /></button>
      </div>
      <PreviewNotices p={p} />
      <PreviewStats p={p} />
      <PreviewTable rows={rows} p={p} />
      <SideList testid="closing-preview-on-hold" title={`On hold — below 50 Self SP (${p.on_hold_count || 0})`} tone="red"
        note="Not paid in this closing. Income and pair / bonus carry-forward are kept and released after the member reaches 50 Self SP."
        items={(p.on_hold || []).map((h) => [h.user_code, h.name, `Self SP ${sp(h.self_sp)} · needs ${sp(h.self_sp_needed)} · would be ${inrC(h.net_if_paid_rs)} net`])} />
      <SideList testid="closing-preview-startup-only" title={`Startup only — already in wallet (${(p.startup_only || []).length})`} tone="slate"
        note="Startup income is credited to the wallet directly. Shown for information; it is not part of this closing."
        items={(p.startup_only || []).map((s) => [s.user_code, s.name, `Startup (already in wallet) ${inrC(s.startup_in_wallet_rs)}`])} />
      <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap bg-white">
        <div className="text-xs text-navy/80">
          {done ? "This week is already finalized — nothing to confirm." : rows.length === 0 ? "No payable members for this week." : "Check every member above, then confirm. Payment is marked paid separately after the bank transfer."}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <XlsxButton label={p.period_label} testid="closing-preview-xlsx" />
        <button
          type="button"
          onClick={() => setAsking(true)}
          disabled={!!done || rows.length === 0}
          data-testid="closing-confirm-btn"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 bg-royal text-white hover:bg-royal-deep rounded-sm disabled:opacity-40 transition-colors"
        >
          <CheckCircle2 size={13} /> Confirm Weekly Closing
        </button>
        </div>
      </div>
      {asking ? <ConfirmModal p={p} onCancel={() => setAsking(false)} onConfirmed={onConfirmed} /> : null}
    </div>
  );
}

function PreviewNotices({ p }) {
  const done = p.already_finalized;
  if (!done && !p.window_open) return null;
  return (
    <div className="px-5 pt-3 space-y-2">
      {done ? (
        <div className="flex items-start gap-2 text-xs border border-amber-300 bg-amber-50 text-amber-900 rounded-sm px-3 py-2" data-testid="closing-preview-already">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>Already finalized: {done.members} members · net {inrC(done.total_net_rs)} · {done.status === "paid" ? "Paid" : "Closing finalized · Payment pending"}.</span>
        </div>
      ) : null}
      {p.window_open && !done ? (
        <div className="flex items-start gap-2 text-xs border border-amber-300 bg-amber-50 text-amber-900 rounded-sm px-3 py-2" data-testid="closing-preview-window-open">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>This week is still running until {fmtIst(p.window_end_ist)}. Income earned after you confirm and before then will not be counted in the next closing. Confirm after the week ends.</span>
        </div>
      ) : null}
    </div>
  );
}

function PreviewStats({ p }) {
  const items = [
    ["Members payable", p.would_insert_rows ?? 0, "closing-stat-members"],
    ["Gross", inrC(p.total_gross_rs), "closing-stat-gross"],
    ["Manual payout deducted", inrC(p.total_withdrawn_deduction_rs), "closing-stat-withdrawn"],
    ["TDS 2%", inrC(p.total_tds_rs), "closing-stat-tds"],
    ["Net payable", inrC(p.total_net_rs), "closing-stat-net"],
    ["On hold (net)", `${p.on_hold_count || 0} · ${inrC(p.on_hold_total_net_rs)}`, "closing-stat-hold"],
    ["Startup (already in wallet)", inrC(p.total_startup_in_wallet_rs), "closing-stat-startup"],
  ];
  return (
    <div className="px-5 py-3 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
      {items.map(([label, value, id]) => (
        <div key={id} className="bg-white border border-border rounded-sm px-3 py-2">
          <div className="text-[10px] uppercase tracking-widest text-navy/70">{label}</div>
          <div className={`text-base font-bold tabular-nums ${id === "closing-stat-net" ? "text-emerald-700" : "text-navy"}`} data-testid={id}>{value}</div>
        </div>
      ))}
    </div>
  );
}

const COLS = ["IBA", "Pair", "Direct Ref", "Monthly Bonus", "Other", "Startup (already in wallet)", "Gross", "Manual payout deducted", "TDS 2%", "Net payable", "Pair CF L / R", "Bonus CF L / R"];

function PreviewTable({ rows, p }) {
  if (!rows.length) return null;
  const td = "px-3 py-2 text-right tabular-nums whitespace-nowrap";
  return (
    <div className="px-5 pb-3">
      <div className="overflow-x-auto bg-white border border-border rounded-sm">
        <table className="w-full text-xs min-w-[1150px]" data-testid="closing-preview-table">
          <thead className="bg-navy text-white">
            <tr>{COLS.map((h, i) => <th key={h} className={`${i ? "text-right" : "text-left"} px-3 py-2 text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap`}>{h}</th>)}</tr>
          </thead>
          <tbody className="text-navy">
            {rows.map((r) => (
              <tr key={r.user_code} className="border-t border-border/70 hover:bg-slate-50 transition-colors" data-testid={`closing-preview-row-${r.user_code}`}>
                <td className="px-3 py-2"><div className="font-mono font-bold">{r.user_code}</div><div className="text-[10px] text-navy/70 truncate max-w-[170px]">{r.name}</div></td>
                <td className={td}>{inrC(r.pair_income_rs)}{r.matched_sp ? <div className="text-[10px] text-navy/60">{sp(r.matched_sp)} SP</div> : null}</td>
                <td className={td}>{inrC(r.direct_ref_rs)}</td>
                <td className={td}>{inrC(r.monthly_bonus_rs)}{r.bonus_level ? <div className="text-[10px] text-navy/60">Level {r.bonus_level}</div> : null}</td>
                <td className={td}>{inrC(r.other_income_rs)}</td>
                <td className={`${td} italic text-navy/60`} data-testid={`closing-preview-startup-${r.user_code}`}>{inrC(r.startup_in_wallet_rs)}<div className="text-[10px] not-italic">not added</div></td>
                <td className={`${td} font-semibold`}>{inrC(r.gross_rs)}</td>
                <td className={`${td} text-red-700`} data-testid={`closing-preview-withdrawn-${r.user_code}`}>{r.withdrawn_deduction_rs ? `− ${inrC(r.withdrawn_deduction_rs)}` : "—"}</td>
                <td className={`${td} text-red-700`}>− {inrC(r.tds_amount_rs)}</td>
                <td className={`${td} font-bold text-emerald-700`} data-testid={`closing-preview-net-${r.user_code}`}>{inrC(r.net_rs)}</td>
                <td className={td}>{lr(r.pair_carry)}</td>
                <td className={td}>{lr(r.bonus_carry)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-navy/30 bg-slate-100 font-bold" data-testid="closing-preview-totals">
              <td className="px-3 py-2">Total · {rows.length}</td>
              {["pair_income_rs", "direct_ref_rs", "monthly_bonus_rs", "other_income_rs", "startup_in_wallet_rs", "gross_rs", "withdrawn_deduction_rs", "tds_amount_rs", "net_rs"].map((k) => (
                <td key={k} className={`${td} ${k === "net_rs" ? "text-emerald-700" : ""}`}>{inrC(rows.reduce((s, r) => s + Number(r[k] || 0), 0))}</td>
              ))}
              <td colSpan={2} className="px-3 py-2 text-right text-[10px] text-navy/70 font-medium">net {inrC(p.total_net_rs)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SideList({ title, note, items, tone, testid }) {
  if (!items.length) return null;
  const cls = tone === "red" ? "border-red-200 bg-red-50 text-red-900" : "border-slate-200 bg-white text-navy";
  return (
    <div className="px-5 pb-3" data-testid={testid}>
      <div className={`border rounded-sm px-3 py-2 ${cls}`}>
        <div className="text-[11px] font-bold uppercase tracking-wider">{title}</div>
        <div className="text-[11px] mt-0.5 opacity-90">{note}</div>
        <ul className="mt-1.5 space-y-0.5 text-xs">
          {items.map(([code, name, text]) => (
            <li key={code}><span className="font-mono font-bold">{code}</span> {name} · {text}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ConfirmModal({ p, onCancel, onConfirmed }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/weekly-closing/confirm", {
        period_label: p.period_label, confirm_text: text,
        expected_rows: p.would_insert_rows, expected_total_net_rs: p.total_net_rs,
      });
      onConfirmed(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Confirm failed");
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm" onClick={onCancel} data-testid="closing-confirm-modal">
      <div className="w-full max-w-md bg-white rounded-sm border border-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border">
          <div className="text-[10px] uppercase tracking-widest text-royal font-bold">Confirm Weekly Closing</div>
          <h3 className="font-serif text-lg text-navy">{fmtPeriodC(p.period_label)}</h3>
        </div>
        <div className="p-5 space-y-3 text-sm text-navy">
          <p>Saves <strong>{p.would_insert_rows}</strong> member results · net payable <strong>{inrC(p.total_net_rs)}</strong>. Members will see <strong>Closing finalized · Payment pending</strong> until it is marked paid.</p>
          <p className="text-xs text-navy/80">Pair and bonus SP used in this closing are consumed. This cannot be undone from this screen.</p>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type CONFIRM" data-testid="closing-confirm-input"
            className="w-full border border-border rounded-sm px-3 py-2 text-sm font-mono uppercase focus:border-royal outline-none" />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={onCancel} data-testid="closing-confirm-cancel" className="px-4 py-2 text-xs text-navy/70 hover:text-navy">Cancel</button>
            <button type="button" onClick={submit} disabled={busy || text.trim().toUpperCase() !== "CONFIRM"} data-testid="closing-confirm-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-royal text-white hover:bg-royal-deep rounded-sm disabled:opacity-40 transition-colors">
              {busy ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} {busy ? "Saving…" : "Confirm Weekly Closing"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
